
import React, { useEffect } from "react";
import { useQuery } from "@tanstack/react-query";
import { useAuth } from "@/contexts/auth";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { MessageSquare, TestTube, CreditCard, AlertCircle } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { fetchMyReagents } from "@/services/myReagents/fetchMyReagents";
import { Link } from "react-router-dom";

const DashboardStats = () => {
  const { user, profile } = useAuth();

  // Fetch user's reagents
  const { data: myReagents = [] } = useQuery({
    queryKey: ['myReagents'],
    queryFn: fetchMyReagents,
    enabled: !!user,
    staleTime: 1000 * 60 * 5, // 5 minutes
  });

  // Fetch unread messages count
  const { data: unreadMessages = 0, refetch: refetchMessages } = useQuery({
    queryKey: ['unreadMessagesCount'],
    queryFn: async () => {
      if (!user) return 0;
      
      const { data, error } = await supabase
        .from('messages')
        .select('id', { count: 'exact' })
        .eq('recipient_id', user.id)
        .eq('read', false);
        
      if (error) {
        console.error("Error fetching unread messages:", error);
        return 0;
      }
      
      return data?.length || 0;
    },
    enabled: !!user,
    staleTime: 1000 * 60, // 1 minute
  });

  // Fetch received swap requests count
  const { data: swapRequests = 0, refetch: refetchSwapRequests } = useQuery({
    queryKey: ['pendingSwapRequests'],
    queryFn: async () => {
      if (!user) return 0;
      
      // First get the user's reagent IDs
      const { data: userReagents, error: reagentsError } = await supabase
        .from('reagents')
        .select('id')
        .eq('user_id', user.id);
        
      if (reagentsError || !userReagents?.length) {
        console.error("Error fetching user reagents:", reagentsError);
        return 0;
      }
      
      const reagentIds = userReagents.map(r => r.id);
      
      // Then count swap requests for those reagents
      const { data, error } = await supabase
        .from('swap_requests')
        .select('id', { count: 'exact' })
        .in('reagent_id', reagentIds)
        .eq('status', 'pending');
        
      if (error) {
        console.error("Error fetching swap requests:", error);
        return 0;
      }
      
      return data?.length || 0;
    },
    enabled: !!user,
    staleTime: 1000 * 60 * 5, // 5 minutes
  });

  // Set up polling for updates
  useEffect(() => {
    const interval = setInterval(() => {
      if (user) {
        refetchMessages();
        refetchSwapRequests();
      }
    }, 60000); // Poll every minute
    
    return () => clearInterval(interval);
  }, [user, refetchMessages, refetchSwapRequests]);

  return (
    <div className="bg-gradient-to-r from-blue-50 to-indigo-50 py-6">
      <div className="container mx-auto px-4">
        <h2 className="text-xl font-bold mb-4">Your Dashboard</h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          <Link to="/messages" className="block">
            <Card className="hover:shadow-md transition-shadow cursor-pointer h-full">
              <CardContent className="p-4 flex items-center space-x-4">
                <div className="bg-blue-100 p-3 rounded-full">
                  <MessageSquare className="h-6 w-6 text-blue-600" />
                </div>
                <div>
                  <p className="text-sm text-gray-500">Unread Messages</p>
                  <div className="flex items-center">
                    <p className="text-2xl font-bold">{unreadMessages}</p>
                    {unreadMessages > 0 && (
                      <Badge variant="destructive" className="ml-2">New</Badge>
                    )}
                  </div>
                </div>
              </CardContent>
            </Card>
          </Link>
          
          <Link to="/my-reagents" className="block">
            <Card className="hover:shadow-md transition-shadow cursor-pointer h-full">
              <CardContent className="p-4 flex items-center space-x-4">
                <div className="bg-green-100 p-3 rounded-full">
                  <AlertCircle className="h-6 w-6 text-green-600" />
                </div>
                <div>
                  <p className="text-sm text-gray-500">Swap Requests</p>
                  <div className="flex items-center">
                    <p className="text-2xl font-bold">{swapRequests}</p>
                    {swapRequests > 0 && (
                      <Badge variant="default" className="ml-2 bg-green-500">Pending</Badge>
                    )}
                  </div>
                </div>
              </CardContent>
            </Card>
          </Link>
          
          <Link to="/profile" className="block">
            <Card className="hover:shadow-md transition-shadow cursor-pointer h-full">
              <CardContent className="p-4 flex items-center space-x-4">
                <div className="bg-yellow-100 p-3 rounded-full">
                  <CreditCard className="h-6 w-6 text-yellow-600" />
                </div>
                <div>
                  <p className="text-sm text-gray-500">Credits Balance</p>
                  <p className="text-2xl font-bold">{profile?.credit_balance || 0}</p>
                </div>
              </CardContent>
            </Card>
          </Link>
          
          <Link to="/my-reagents" className="block">
            <Card className="hover:shadow-md transition-shadow cursor-pointer h-full">
              <CardContent className="p-4 flex items-center space-x-4">
                <div className="bg-purple-100 p-3 rounded-full">
                  <TestTube className="h-6 w-6 text-purple-600" />
                </div>
                <div>
                  <p className="text-sm text-gray-500">Your Reagents</p>
                  <p className="text-2xl font-bold">{myReagents.length}</p>
                </div>
              </CardContent>
            </Card>
          </Link>
        </div>
      </div>
    </div>
  );
};

export default DashboardStats;
